/**
 * Platform-admin runtime settings (`dbo.app_config`) — /api/admin/settings*.
 * Gated server-side on role === 'platform' (not `users.manage`: customer-company
 * admins hold that, and this edits Rebound's own mail infrastructure).
 *
 * Secrets are write-only across this boundary: GET returns `is_set` plus a null
 * `value` for them, and PUT treats an omitted/blank secret as "leave the stored
 * value alone". That is what lets the form show an empty password box without
 * wiping the credential every time an unrelated field is saved.
 */
import { apiFetch } from "./apiClient";

export type SettingValueType = "string" | "int" | "bool";

export interface AdminSetting {
  id: string; // "<Section>.<Key>"
  section: string;
  key: string;
  label: string;
  value_type: SettingValueType;
  secret: boolean;
  help: string;
  /** Which settings tab renders it: 'mail' (Mail) or 'system' (System). */
  category: "mail" | "system";
  /** True when a real (non-blank, non-REPLACE_ME) value is stored. */
  is_set: boolean;
  /** Current value for non-secrets; null for secrets. */
  value: string | null;
}

export interface SettingsUpdateResponse {
  updated: string[];
  unchanged: string[];
}

export interface TestEmailResult {
  delivered: boolean;
  to: string;
  via: string;
  detail: string;
}

async function req<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  return apiFetch<T>(path, token, init);
}

export async function listSettings(token: string): Promise<AdminSetting[]> {
  const body = await req<{ settings: AdminSetting[] }>(token, "/api/admin/settings");
  return body.settings ?? [];
}

/**
 * Update settings. `values` is keyed by setting id ("Section.Key").
 *
 * Omit a secret (or pass "") to keep its stored value. The server rejects any id
 * outside its allow-list with a 400 rather than ignoring it, so a typo surfaces
 * instead of looking like a successful save.
 */
export function updateSettings(
  token: string,
  values: Record<string, string>,
): Promise<SettingsUpdateResponse> {
  return req<SettingsUpdateResponse>(token, "/api/admin/settings", {
    method: "PUT",
    body: JSON.stringify({ values }),
  });
}

/** Send a test email. The server always mails the caller, never a passed address. */
export function sendTestEmail(token: string): Promise<TestEmailResult> {
  return req<TestEmailResult>(token, "/api/admin/settings/test-email", {
    method: "POST",
  });
}
